import type { ReactNode } from "react";
import { ArrowLeft } from "lucide-react";
import { SITE_URL as PUBLIC_SITE_URL } from "@/lib/use-seo";
import styles from "./InquiryShell.module.css";

/**
 * Full-bleed shell for the public inquiry flow. The inquiry pages used to
 * float inside a framed card on a stone backdrop; the flow is now the page
 * itself — white background, a slim company header bar, a narrow left
 * column for the steps, and an optional sticky right rail (the "Your
 * event" panel) separated by a hairline instead of its own bordered box.
 * There is one layout, and terminal screens (confirmation, quote status)
 * use it too: they keep the company header and the
 * "Powered by HireOS · Privacy" line, and they top-anchor like a step.
 * The old `centered` variant stripped both, so the screen a Customer lands
 * on right after handing over their details was the one screen with no way
 * to check who sent it.
 */
interface InquiryShellProps {
  children: ReactNode;
  /** Sticky right rail at ≥lg (hidden below — mobile has the pinned bar). */
  aside?: ReactNode;
  /** Company identity for the header bar; `url` makes the name + back link
   *  point at the company's own site. Header renders only when provided. */
  company?: { name: string; url?: string };
}

function PoweredByFooter(): React.JSX.Element {
  return (
    // The mark is a link now (#221): the apex serves a real identity page, so
    // the premise for keeping it plain text — "no HireOS site exists yet" —
    // no longer holds, and a page showing a Company's name and bank details
    // with no way to check who sent it is what Safe Browsing reads as
    // impersonation. Privacy sits beside it for the same reason: it was a
    // routed page nothing linked to.
    <footer className={styles.footer}>
      <p className={styles.footerText}>
        <a href={PUBLIC_SITE_URL} className={styles.footerLink}>
          Powered by HireOS
        </a>
        <span className={styles.footerSeparator}>·</span>
        <a href="/privacy" className={styles.footerLink}>
          Privacy
        </a>
      </p>
    </footer>
  );
}

function CompanyHeader({
  company,
}: {
  company: { name: string; url?: string };
}): React.JSX.Element {
  return (
    <header className={styles.header}>
      <div className={styles.headerInner}>
        {company.url ? (
          <a href={company.url} className={styles.companyLink}>
            <ArrowLeft className={styles.backIcon} />
            {company.name}
          </a>
        ) : (
          <p className={styles.companyName}>{company.name}</p>
        )}
      </div>
    </header>
  );
}

export function InquiryShell({
  children,
  aside,
  company,
}: InquiryShellProps): React.JSX.Element {
  return (
    <div className={styles.shell}>
      {company && <CompanyHeader company={company} />}
      <main className={styles.main}>
        <div
          className={
            aside ? `${styles.container} ${styles.withAside}` : styles.container
          }
        >
          <div className={styles.column}>{children}</div>
          {aside && <aside className={styles.aside}>{aside}</aside>}
        </div>
      </main>
      <PoweredByFooter />
    </div>
  );
}
